import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Checkbox, { CheckboxPresentationProps, CheckboxProps } from "@insite/mobius/Checkbox";
import { ClickableProps } from "@insite/mobius/Clickable";
import DataTable, { DataTableProps, SortOrderOptions } from "@insite/mobius/DataTable";
import DataTableBody from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableHead from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import DynamicDropdown, {
    DynamicDropdownPresentationProps,
    DynamicDropdownProps,
    OptionObject,
} from "@insite/mobius/DynamicDropdown";
import { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Select, { SelectPresentationProps, SelectProps } from "@insite/mobius/Select";

import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext, { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import HistoryContext, { HasHistory, useHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { PunchoutUserIdentityModel, UserIdentityCollection, UserIdentityModel } from "@TKCCP/Types/ApiModels";
import React, { ChangeEvent, FC, useEffect, useState } from "react";
import { ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    userIdentityModel: PunchoutUserIdentityModel[] | undefined;
    savePunchoutIdentityUsers(userIdentityCollection: UserIdentityCollection): void;
}

type Props = OwnProps; // & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface PunchoutIdentityTableStyles {
    container?: InjectableCss;
    headerClickable?: ClickableProps;
    headerText?: TypographyProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    addButton?: ButtonPresentationProps;
    dataTable?: DataTableProps;
    listsSelect?: DynamicDropdownPresentationProps;
    saveButton?: ButtonPresentationProps;
    cancelButton?: ButtonPresentationProps;
    btnContainer?: InjectableCss;
    customerNumberHeader: DataTableHeaderProps;
    customerIdentityHeader?: DataTableHeaderProps;
    customerSecretHeader?: DataTableHeaderProps;
    customerStatusHeader?: DataTableHeaderProps;
    customerNumberCells: DataTableCellProps;
    customerIdentityCells?: DataTableCellProps;
    customerSecretCells?: DataTableCellProps;
    customerStatusCells?: DataTableCellProps;
    customerGenerateKeyCells?: DataTableCellProps;
}

export const PunchoutIdentityTableStyles: PunchoutIdentityTableStyles = {
    container: {
        css: css`
            overflow: auto;
            min-height: 252px;
            margin-bottom: 25px;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 300px;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
    noResultsContainer: {
        css: css`
            text-align: center;
            padding: 20px;
        `,
    },
    noResultsText: {
        variant: "h4",
    },
    headerClickable: {
        css: css`
            width: 100%;
            justify-content: space-between;
        `,
    },
    addButton: {
        color: "secondary",
        sizeVariant: "medium",
    },
    saveButton: {
        variant: "primary",
        css: css`
            margin-right: 15px;
        `,
    },
    cancelButton: {
        variant: "secondary",
    },
    btnContainer: {
        css: css`
            text-align: center;
        `,
    },
    dataTable: {
        cssOverrides: {
            table: css`
                min-width: 900px;
                margin: 20px 0 0;
                td {
                    padding: 4px 12px;
                }
                tbody {
                    tr {
                        &:last-child,
                        &:nth-last-child(2n) {
                            ul {
                                bottom: 43px;
                                top: auto;
                            }
                        }
                        &:first-child {
                            ul {
                                top: 39px;
                                bottom: auto;
                            }
                        }
                        ul {
                            margin-bottom: 0;
                        }
                    }
                }
            `,
        },
    },
    customerNumberHeader: {},
    customerNumberCells: {
        css: css`
            width: 25%;
            white-space: inherit;
        `,
    },
    customerSecretCells: {
        css: css`
            ul {
                max-height: 123px;
            }
        `,
    },
};

const styles = PunchoutIdentityTableStyles;

const PunchoutUserIdentityGrid: FC<Props> = ({ userIdentityModel, savePunchoutIdentityUsers }) => {
    const history = useHistory();
    const toasterContext = React.useContext(ToasterContext);
    const [userIdentityList, SetUserIdentityList] = useState<PunchoutUserIdentityModel[] | undefined>(
        userIdentityModel,
    );
    const [updatedIds, SetUpdatedIds] = useState<string[]>([]);
    // useEffect(() => {
    //     SetUserIdentityList(userIdentityModel);
    // }, []);

    useEffect(() => {
        SetUserIdentityList(userIdentityModel);
    }, [userIdentityModel]);

    const numbers = "0123456789";
    const upperCaseLetters = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const lowerCaseLetters = "abcdefghijklmnopqrstuvwxyz";
    const today = new Date().toLocaleString().replace(/[&\/\\#,+()$~%.'":*?<>{}" "]/g, "");
    const characterList = numbers + upperCaseLetters + lowerCaseLetters + today;
    const generateRandomNumber = () => {
        let passkey = "";
        const characterListLength = characterList.length;
        for (let i = 0; i < 20; i++) {
            const characterIndex = Math.round(Math.random() * characterListLength);
            passkey = passkey + characterList.charAt(characterIndex);
        }
        return passkey;
    };

    const UpdateupdateIdentityIds = (id: string) => {
        const ids = updatedIds;
        ids.push(id);
        SetUpdatedIds(ids);
    };
    const generateIdentityHandler = (id: string) => {
        // eslint-disable-next-line no-console
        console.log(id);
        const identity = generateRandomNumber();
        updateIdentity(id, identity);
        UpdateupdateIdentityIds(id);
    };
    const updateIdentity = (id: string, uniqueIdentity: string) => {
        const userIdentites = userIdentityList?.map(item => {
            if (item.id === id) {
                return { ...item, identity: uniqueIdentity };
            }
            return item;
        });
        SetUserIdentityList(userIdentites);
        // userIdentityModel = userIdentites;
    };
    const updateSecretId = (id: string, secId: string) => {
        const userIdentites = userIdentityList?.map(item => {
            if (item.id === id) {
                return { ...item, secretId: secId };
            }
            return item;
        });
        SetUserIdentityList(userIdentites);
        // userIdentityModel = userIdentites;
    };
    const updateActive = (id: string, isActive: boolean) => {
        const userIdentites = userIdentityList?.map(item => {
            if (item.id === id) {
                return { ...item, active: isActive };
            }
            return item;
        });
        SetUserIdentityList(userIdentites);
        // userIdentityModel = userIdentites;
    };
    const listChangeHandler = (value?: string) => {
        const arr = value?.split("|");
        if (arr) {
            const [SecretId, id] = arr;
            updateSecretId(id, SecretId);
            UpdateupdateIdentityIds(id);
        }
    };

    const checkboxChangeHandler = (value: boolean, id: string) => {
        updateActive(id, value);
        UpdateupdateIdentityIds(id);
    };
    const validateIdentityList = (filterList: PunchoutUserIdentityModel[] | undefined) => {
        let result = true;
        // eslint-disable-next-line array-callback-return
        filterList?.map(item => {
            if (item.identity === "" || item.secretId === "") {
                // eslint-disable-next-line no-console
                console.log("error");
                result = false;
            }
        });
        return result;
    };

    const saveButtonHandler = () => {
        const uniqueIds = updatedIds.filter((v, i, a) => a.indexOf(v) === i);
        const userIdentityCollection: UserIdentityCollection = {
            userIdentityList: [] as UserIdentityModel[],
        } as UserIdentityCollection;

        const filterList = userIdentityList?.filter(x => uniqueIds.includes(x.id));

        // Validate filter list.
        if (!validateIdentityList(filterList)) {
            toasterContext.addToast({ body: "Identity and Secret combination is mandatory.", messageType: "danger" });
            return false;
        }

        // eslint-disable-next-line no-console
        // console.log(data);

        // eslint-disable-next-line array-callback-return
        filterList?.map(identity => {
            userIdentityCollection.userIdentityList?.push({
                id: identity.id,
                identity: identity.identity,
                active: identity.active,
                userId: identity.userId,
                identityId: identity.identityId,
                customerId: identity.customerId,
                secretId: identity.secretId,
            } as UserIdentityModel);
        });

        savePunchoutIdentityUsers(userIdentityCollection);
    };
    const clearButtonHandler = () => {
        history.push("/MyAccount/PunchoutIdentityPage");
    };

    const valueUpdate = (event: React.ChangeEvent<HTMLInputElement> | any, id: string) =>{
        const updateIdentityList = userIdentityList && userIdentityList?.map((item: any)=>{
            if(item.id === id){
                return { ...item, identity: event.clipboardData.getData('text') }
            }
            return item;
        })
        SetUserIdentityList(updateIdentityList)
    }
    return (
        <StyledWrapper>
            <StyledWrapper {...styles.container} data-test-selector="punchoutSecretGridTable">
                <DataTable {...styles.dataTable}>
                    <DataTableHead>
                        <DataTableHeader {...styles.customerNumberHeader}>{translate("Customer")}</DataTableHeader>
                        <DataTableHeader colSpan={2} {...styles.customerIdentityHeader}>
                            {translate("Identity")}
                        </DataTableHeader>
                        <DataTableHeader {...styles.customerSecretHeader}>{translate("Secret")}</DataTableHeader>
                        <DataTableHeader {...styles.customerStatusHeader}>{translate("Active")}</DataTableHeader>
                    </DataTableHead>
                    <DataTableBody data-test-selector="punchoutSecretGridTable_tableBody">
                        {userIdentityList?.map(
                            ({ id, customerNumber, identity, punchOutSecretList, secretId, active }) => (
                                <DataTableRow key={id}>
                                    <DataTableCell {...styles.customerNumberCells}>{customerNumber}</DataTableCell>
                                    <DataTableCell {...styles.customerIdentityCells}>
                                        <TextField id={`${id}identity`} type="text" value={identity} onPaste={(e)=>{valueUpdate(e, id)}} />
                                    </DataTableCell>
                                    <DataTableCell {...styles.customerGenerateKeyCells}>
                                        <Button {...styles.addButton} onClick={() => generateIdentityHandler(id)}>
                                            {translate("Generate Key")}
                                        </Button>
                                    </DataTableCell>
                                    <DataTableCell {...styles.customerSecretCells}>
                                        {punchOutSecretList && (
                                            <DynamicDropdown
                                                {...styles.listsSelect}
                                                onSelectionChange={listChangeHandler}
                                                selected={`${secretId}|${id}`}
                                                options={punchOutSecretList.map(o => ({
                                                    optionText: o.company,
                                                    optionValue: `${o.id}|${id}`,
                                                }))}
                                                data-test-selector="listChangeHandler"
                                            />
                                        )}
                                    </DataTableCell>
                                    <DataTableCell {...styles.customerStatusCells}>
                                        <Checkbox
                                            checked={active}
                                            onChange={() => checkboxChangeHandler(!active, id)}
                                        ></Checkbox>
                                    </DataTableCell>
                                </DataTableRow>
                            ),
                        )}
                    </DataTableBody>
                </DataTable>
            </StyledWrapper>
            <StyledWrapper {...styles.btnContainer}>
                <Button {...styles.saveButton} onClick={saveButtonHandler}>
                    {translate("Save")}
                </Button>
                <Button {...styles.cancelButton} onClick={clearButtonHandler}>
                    {translate("Cancel")}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default PunchoutUserIdentityGrid;
